import type { ReactNode } from "react";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { toCellText } from "../utils/format";
import { EmptyAlert } from "./EmptyAlert";

/** 見出し付きの表。SPは横に溢れるため1行=1カードのkey-value表示にする */
export const DataTable: React.FC<{
  title: string;
  headers: string[];
  rows: ReactNode[][];
  icon?: ReactNode;
  /** 0件時に表示する文言（既定: 表示するデータがありません） */
  emptyMessage?: string;
}> = ({
  title,
  headers,
  rows,
  icon,
  emptyMessage = "表示するデータがありません",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {icon != null && (
          <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        )}
        <Typography variant="h6">{title}</Typography>
      </Stack>
      {rows.length === 0 ? (
        <EmptyAlert>{emptyMessage}</EmptyAlert>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((row, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1} divider={<Divider flexItem />}>
                {headers.map((header, j) => (
                  <Stack
                    key={header}
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      {header}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "right", overflowWrap: "anywhere" }}
                    >
                      {toCellText(row[j])}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{ whiteSpace: "nowrap", bgcolor: "action.hover" }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  {row.map((cell, j) => (
                    <TableCell key={j} sx={{ whiteSpace: "nowrap" }}>
                      {toCellText(cell)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
